import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Download, RefreshCcw, Inbox, FileSpreadsheet, History } from 'lucide-react';
import { downloadAllLabels, exportDeliveries, exportStatusHistory } from '../../../../../features/deliveries/deliveriesThunks';
import { fetchPendingUpsellsCount } from '../../../../../features/upselling/upsellingThunks';
import { UPSELLING_HIDDEN } from '../../../../../features/upselling/upsellingConstants';
import { selectIsKindRunning } from '../../../../../features/notifications/notificationsSlice';
import { NOTIFICATION_KIND } from '../../../../../features/notifications/notificationsConstants';
import { DropdownMenu } from '../../../../../components/ui/DropdownMenu';

/**
 * The options menu beside the title: what applies to the whole listing. What
 * each item did is said by its toast or by the Activity Center.
 */
export const DeliveryActions = ({ user, isAdmin, filters }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pendingCount } = useSelector((state) => state.upselling);
  const isDownloadingLabels = useSelector((state) => state.deliveries.isDownloadingLabels);

  // Both exports are queued jobs: the answer arrives over the socket, minutes
  // later, so what disables the menu item is the notification still being open
  // — not a local flag that a navigation would reset.
  const isExportingDeliveries = useSelector(
    selectIsKindRunning(NOTIFICATION_KIND.DOWNLOAD_DELIVERIES)
  );
  const isExportingHistory = useSelector(
    selectIsKindRunning(NOTIFICATION_KIND.DOWNLOAD_HISTORY)
  );

  // Not while upselling is hidden: the incoming requests are out of every menu.
  const showsIncoming = isAdmin && !UPSELLING_HIDDEN;

  useEffect(() => {
    if (showsIncoming) dispatch(fetchPendingUpsellsCount());
  }, [showsIncoming, dispatch]);

  const shouldGetTrackingBtn = filters.delivery_status === "no_tracking";

  // --- RENDERING DROPDOWN MENU FOR GLOBAL ACTIONS ---
  const globalActions = [
    {
      // "de esta página": the endpoint always pages, so it never was "todas".
      label: 'Descargar etiquetas de esta página',
      icon: Download,
      onClick: () => dispatch(downloadAllLabels({ filters, user })),
      disabled: isDownloadingLabels,
      description: isDownloadingLabels
        ? 'Armando el PDF con las etiquetas'
        : 'Las de los envíos que ves en la tabla, en un PDF',
    },
    // Only show get tracking if filters match
    ...(shouldGetTrackingBtn ? [{
      label: 'Obtener tracking',
      icon: RefreshCcw,
      onClick: () => console.log('GetTracking modal...')
    }] : []),
    // Admin specific global actions
    ...(showsIncoming ? [{
      label: 'Solicitudes entrantes',
      icon: Inbox,
      onClick: () => navigate('/incoming-upsells'),
      description: pendingCount > 0
        ? `${pendingCount} ${pendingCount === 1 ? 'envío pendiente' : 'envíos pendientes'} de responder`
        : 'Envíos que otros admins te cedieron'
    }] : []),
    {
      label: 'Exportar a Excel',
      icon: FileSpreadsheet,
      onClick: () => dispatch(exportDeliveries()),
      disabled: isExportingDeliveries,
      dividerBefore: true,
      description: isExportingDeliveries
        ? 'Ya hay un reporte en curso, te avisamos al terminar'
        : 'Se genera en segundo plano y llega al Centro de Actividad'
    },
    // The endpoint behind this one is gated with `IsStaffAdminUser`, so an
    // admin who is not staff would only ever get a 403 out of it. Hiding it is
    // the honest option; this is also the first place the new front reads
    // `is_staff`, a field the login has always sent.
    ...(user?.is_staff ? [{
      label: 'Exportar historial de estados',
      icon: History,
      onClick: () => dispatch(exportStatusHistory()),
      disabled: isExportingHistory,
      description: isExportingHistory
        ? 'Ya hay un historial en curso'
        : 'Cada cambio de estado del rango, por correo'
    }] : [])
  ];

  return (
    <div className="flex items-center justify-end">
      <DropdownMenu
        triggerLabel="Opciones"
        items={globalActions}
      />
    </div>
  );
};
